"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useId, useMemo, useState } from "react";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import type { GoogleWorkspaceLinkPreview } from "@/services/api/link-preview-api";
import { LinkPreviewCardLayout } from "./OpenGraphPreview";
interface GoogleWorkspaceCardProps {
  readonly href: string;
  readonly data: GoogleWorkspaceLinkPreview;
}
const PRODUCT_CONFIG: Record<GoogleWorkspaceLinkPreview["type"], {
  readonly badge: string;
  readonly product: string;
  readonly fallbackTitle: string;
}> = {
  "google.docs": {
    badge: "Docs",
    product: "Google Docs",
    fallbackTitle: "Untitled Doc"
  },
  "google.sheets": {
    badge: "Sheets",
    product: "Google Sheets",
    fallbackTitle: "Untitled Sheet"
  },
  "google.slides": {
    badge: "Slides",
    product: "Google Slides",
    fallbackTitle: "Untitled Slides"
  }
};
const getActionButtonClasses = (variant: "primary" | "secondary") => clsx("tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-solid tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-no-underline tw-transition tw-duration-200 focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-primary-400 disabled:tw-cursor-not-allowed disabled:tw-opacity-60", variant === "primary" ? "tw-border-primary-500 tw-bg-primary-500 tw-text-white hover:tw-bg-primary-400" : "tw-border-iron-700 tw-bg-transparent tw-text-iron-100 hover:tw-border-primary-400 hover:tw-text-primary-300");
export default function GoogleWorkspaceCard(t0) {
  const $ = _c(66);
  const {
    href,
    data
  } = t0;
  const previewContainerId = useId();
  const [showPreview, setShowPreview] = useState(false);
  const [imageError, setImageError] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setImageError(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== data.thumbnail) {
    t2 = [data.thumbnail];
    $[1] = data.thumbnail;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[3] !== showPreview) {
    t3 = () => {
      if (!showPreview) {
        return;
      }
      const handleKeyDown = event => {
        if (event.key === "Escape") {
          event.preventDefault();
          setShowPreview(false);
        }
      };
      if (typeof globalThis.addEventListener !== "function") {
        return;
      }
      globalThis.addEventListener("keydown", handleKeyDown);
      return () => {
        globalThis.removeEventListener?.("keydown", handleKeyDown);
      };
    };
    t4 = [showPreview];
    $[3] = showPreview;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  const productConfig = PRODUCT_CONFIG[data.type];
  const productName = productConfig.product;
  let t5;
  bb0: {
    if (typeof data.title === "string" && data.title.trim().length > 0) {
      let t6;
      if ($[6] !== data.title) {
        t6 = data.title.trim();
        $[6] = data.title;
        $[7] = t6;
      } else {
        t6 = $[7];
      }
      t5 = t6;
      break bb0;
    }
    t5 = productConfig.fallbackTitle;
  }
  const displayTitle = t5;
  const thumbnailAlt = `Preview image of ${productName}: ${displayTitle}`;
  let t6;
  bb1: {
    const rawEmbedUrl = "embedPub" in data.links && typeof data.links.embedPub === "string" ? data.links.embedPub : undefined;
    if (rawEmbedUrl && rawEmbedUrl.length > 0) {
      t6 = rawEmbedUrl;
      break bb1;
    }
    t6 = typeof data.links.preview === "string" && data.links.preview.length > 0 ? data.links.preview : undefined;
  }
  const previewUrl = t6;
  const canShowPreview = Boolean(previewUrl);
  const viewLiveActive = showPreview && canShowPreview;
  const activePreviewUrl = viewLiveActive ? previewUrl : undefined;
  let t7;
  try {
    let t8;
    if ($[8] !== data.links.open) {
      const parsed = new URL(data.links.open);
      let t9;
      if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = /^www\./i;
        $[10] = t9;
      } else {
        t9 = $[10];
      }
      t8 = parsed.hostname.replace(t9, "");
      $[8] = data.links.open;
      $[9] = t8;
    } else {
      t8 = $[9];
    }
    t7 = t8;
  } catch {
    t7 = "docs.google.com";
  }
  const domain = t7;
  const openLabel = `Open in ${productName}`;
  const downloadUrl = "exportPdf" in data.links && typeof data.links.exportPdf === "string" ? data.links.exportPdf : undefined;
  let t8;
  if ($[11] !== canShowPreview) {
    t8 = () => {
      if (!canShowPreview) {
        return;
      }
      setShowPreview(_temp);
    };
    $[11] = canShowPreview;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const handleTogglePreview = t8;
  let t9;
  if ($[13] !== data.thumbnail || $[14] !== imageError || $[15] !== productConfig.badge || $[16] !== thumbnailAlt) {
    t9 = <div className="md:tw-w-64 md:tw-flex-shrink-0"><div className="tw-relative tw-aspect-video tw-w-full tw-overflow-hidden tw-rounded-lg tw-bg-iron-900/60">{data.thumbnail && !imageError ? <Image src={data.thumbnail} alt={thumbnailAlt} fill={true} className="tw-h-full tw-w-full tw-object-cover" sizes="(max-width: 768px) 100vw, 256px" loading="lazy" unoptimized={true} onError={() => setImageError(true)} /> : <div className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-iron-800/80"><span className="tw-text-sm tw-font-semibold tw-text-iron-200">{productConfig.badge}</span></div>}</div></div>;
    $[13] = data.thumbnail;
    $[14] = imageError;
    $[15] = productConfig.badge;
    $[16] = thumbnailAlt;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== productConfig.badge) {
    t10 = <span className="tw-inline-flex tw-items-center tw-rounded-full tw-bg-iron-800/80 tw-px-3 tw-py-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-iron-200">{productConfig.badge}</span>;
    $[18] = productConfig.badge;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== domain) {
    t11 = <span className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-iron-400">{domain}</span>;
    $[20] = domain;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t10 || $[23] !== t11) {
    t12 = <div className="tw-flex tw-items-center tw-gap-2">{t10}{t11}</div>;
    $[22] = t10;
    $[23] = t11;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== displayTitle) {
    t13 = <h3 className="tw-line-clamp-2 tw-text-lg tw-font-semibold tw-leading-snug tw-text-iron-100">{displayTitle}</h3>;
    $[25] = displayTitle;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] !== data.availability || $[28] !== productName) {
    t14 = data.availability === "restricted" ? <p className="tw-m-0 tw-text-sm tw-text-amber-300">This file may require permission to view. You can still open it directly in {productName}.</p> : null;
    $[27] = data.availability;
    $[28] = productName;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  let t15;
  if ($[30] !== t12 || $[31] !== t13 || $[32] !== t14) {
    t15 = <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-gap-3">{t12}{t13}{t14}</div>;
    $[30] = t12;
    $[31] = t13;
    $[32] = t14;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== t15 || $[35] !== t9) {
    t16 = <div className="tw-flex tw-flex-col tw-gap-4 md:tw-flex-row">{t9}{t15}</div>;
    $[34] = t15;
    $[35] = t9;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const t17 = data.links.open;
  let t18;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = getActionButtonClasses("primary");
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  const t19 = `${openLabel} (opens in a new tab)`;
  let t20;
  if ($[38] !== data.links.open || $[39] !== openLabel || $[40] !== t19) {
    t20 = <Link href={t17} target="_blank" rel="noopener noreferrer" prefetch={false} className={t18} aria-label={t19}>{openLabel}</Link>;
    $[38] = data.links.open;
    $[39] = openLabel;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  let t21;
  if ($[42] !== canShowPreview || $[43] !== handleTogglePreview || $[44] !== previewContainerId || $[45] !== productName || $[46] !== viewLiveActive) {
    t21 = canShowPreview ? <button type="button" onClick={handleTogglePreview} className={getActionButtonClasses("secondary")} aria-controls={previewContainerId} aria-expanded={viewLiveActive} aria-pressed={viewLiveActive} aria-label={`${viewLiveActive ? "Hide" : "View"} live preview for ${productName}`}>{viewLiveActive ? "Hide live preview" : "View live preview"}</button> : null;
    $[42] = canShowPreview;
    $[43] = handleTogglePreview;
    $[44] = previewContainerId;
    $[45] = productName;
    $[46] = viewLiveActive;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== downloadUrl || $[49] !== productName) {
    t22 = downloadUrl ? <Link href={downloadUrl} target="_blank" rel="noopener noreferrer" prefetch={false} className={getActionButtonClasses("secondary")} aria-label={`Download ${productName} as PDF (opens in a new tab)`}>Download PDF</Link> : null;
    $[48] = downloadUrl;
    $[49] = productName;
    $[50] = t22;
  } else {
    t22 = $[50];
  }
  let t23;
  if ($[51] !== t20 || $[52] !== t21 || $[53] !== t22) {
    t23 = <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">{t20}{t21}{t22}</div>;
    $[51] = t20;
    $[52] = t21;
    $[53] = t22;
    $[54] = t23;
  } else {
    t23 = $[54];
  }
  let t24;
  if ($[55] !== activePreviewUrl || $[56] !== previewContainerId || $[57] !== productName) {
    t24 = activePreviewUrl ? <div className="tw-space-y-3" id={previewContainerId} role="region" aria-label={`${productName} live preview`}><div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/30 tw-p-2"><iframe src={activePreviewUrl} title={`${productName} live preview`} className="tw-h-72 tw-w-full tw-rounded tw-border-0 tw-bg-iron-900" sandbox="allow-scripts allow-same-origin allow-presentation" referrerPolicy="no-referrer" loading="lazy" allowFullScreen={true} /></div></div> : null;
    $[55] = activePreviewUrl;
    $[56] = previewContainerId;
    $[57] = productName;
    $[58] = t24;
  } else {
    t24 = $[58];
  }
  let t25;
  if ($[59] !== t16 || $[60] !== t23 || $[61] !== t24) {
    t25 = <div className="tw-rounded-xl tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-900/40 tw-p-4"><div className="tw-flex tw-flex-col tw-gap-4">{t16}{t23}{t24}</div></div>;
    $[59] = t16;
    $[60] = t23;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== href || $[64] !== t25) {
    t26 = <LinkPreviewCardLayout href={href}>{t25}</LinkPreviewCardLayout>;
    $[63] = href;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  return t26;
}
function _temp(prev) {
  return !prev;
}